import React, { useState, useCallback } from 'react';
import { useReactFlow } from 'reactflow';

interface Annotation {
  id: string;
  text: string;
  position: { x: number; y: number };
  color: string;
  nodeId?: string;
}

interface GraphAnnotationProps {
  sessionId: string;
  onAddAnnotation: (annotation: Annotation) => void;
  onDeleteAnnotation: (annotationId: string) => void;
}

export const GraphAnnotation: React.FC<GraphAnnotationProps> = ({
  sessionId,
  onAddAnnotation,
  onDeleteAnnotation
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [annotationText, setAnnotationText] = useState('');
  const [annotationColor, setAnnotationColor] = useState('#FFEB3B'); // Yellow default
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(undefined);
  const { getNodes, screenToFlowPosition } = useReactFlow();
  
  // Get all nodes for the node selector
  const nodes = getNodes();
  
  // Handle adding a new annotation
  const handleAddAnnotation = useCallback(() => {
    if (!annotationText.trim()) return;
    
    // Create a new annotation
    const newAnnotation: Annotation = {
      id: `annotation-${Date.now()}`,
      text: annotationText,
      position: selectedNodeId 
        ? getNodePosition(selectedNodeId)
        : { x: 100, y: 100 }, // Default position if no node selected
      color: annotationColor,
      nodeId: selectedNodeId
    };
    
    onAddAnnotation(newAnnotation);
    
    // Reset form
    setAnnotationText('');
    setSelectedNodeId(undefined);
    setIsAdding(false);
  }, [annotationText, annotationColor, selectedNodeId, onAddAnnotation, getNodePosition]);
  
  // Get position for a node (centered above the node)
  const getNodePosition = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    
    if (node) {
      return {
        x: node.position.x + (node.width || 0) / 2,
        y: node.position.y - 40 // Position above the node
      };
    }
    
    return { x: 100, y: 100 }; // Default position
  }, [nodes]);
  
  return (
    <div className="graph-annotation relative">
      <button
        className={`px-3 py-1 ${
          isAdding ? 'bg-red-600' : 'bg-blue-600'
        } text-white rounded-md hover:${
          isAdding ? 'bg-red-700' : 'bg-blue-700'
        } flex items-center`}
        onClick={() => setIsAdding(!isAdding)}
      >
        {isAdding ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Cancel
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            Add Annotation
          </>
        )}
      </button>
      
      {isAdding && (
        <div className="absolute top-12 right-0 bg-white rounded-lg shadow-lg p-4 z-20 w-80">
          <h3 className="font-medium mb-3">Add Annotation</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Annotation Text
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                value={annotationText}
                onChange={(e) => setAnnotationText(e.target.value)}
                placeholder="Enter annotation text..."
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <div className="flex space-x-2">
                {['#FFEB3B', '#4CAF50', '#2196F3', '#F44336', '#9C27B0'].map(color => (
                  <button
                    key={color}
                    className={`w-6 h-6 rounded-full ${
                      annotationColor === color ? 'ring-2 ring-offset-2 ring-gray-500' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setAnnotationColor(color)}
                  ></button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attach to Node (Optional)
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedNodeId || ''}
                onChange={(e) => setSelectedNodeId(e.target.value || undefined)}
              >
                <option value="">None (Floating Annotation)</option>
                {nodes.map(node => (
                  <option key={node.id} value={node.id}>
                    {node.data?.label || node.id}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={handleAddAnnotation}
                disabled={!annotationText.trim()}
              >
                Add Annotation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
